import { isToolUIPart } from 'ai';

/**
 * Pure transcript-preparation logic for the Distro AI chat, extracted from
 * FloatingAIChat so it can be unit tested: which slice of history to send,
 * whether a stored compaction block can be replayed, and how to sanitize tool
 * parts interrupted by a stop. Every rule in here exists because a provider
 * rejects the alternative — see the comments on each function.
 */

type TranscriptMessage = { id: string; role: string; parts?: any[] };

export type OpenAICompactionData = { id: string; encrypted_content: string; model?: string };

// Remove compaction artifacts that can't be replayed against the outgoing
// model: OpenAI blocks ride a data part, Anthropic ones ride providerMetadata
// on a text part. Assistant messages left with only data-* parts are dropped —
// convertToModelMessages turns them into empty assistant messages, which
// providers can reject with a 400.
export const stripCompactionArtifacts = <M extends TranscriptMessage>(msgs: M[]): M[] => msgs
  .map((m) => m.role === 'assistant' && m.parts
    ? {
      ...m,
      parts: m.parts.filter((p: any) => p.type !== 'data-openai-compaction'
        && p.providerMetadata?.anthropic?.type !== 'compaction'),
    }
    : m)
  .filter((m) => m.role !== 'assistant'
    || (m.parts?.some((p: any) => !String(p.type).startsWith('data-')) ?? false));

// Clean up tool parts interrupted by a stop (or any abandoned session): parts
// still streaming their input carry partial input that can't be safely
// converted for the next request, so they're removed — along with the
// reasoning parts directly before them, since OpenAI Responses rejects a
// reasoning item whose following item is missing. Emptied assistant messages
// are dropped. Parts with complete input but no output are marked errored,
// which converts to a valid tool_use + error result pair.
export const cleanupInterruptedToolParts = <M extends TranscriptMessage>(msgs: M[]): M[] => msgs
  .map((m) => {
    const incomplete = (p: any) => isToolUIPart(p)
      && p.state !== 'output-available' && p.state !== 'output-error';
    if (m.role !== 'assistant' || !m.parts?.some(incomplete)) return m;
    const parts: any[] = [];
    for (const p of m.parts) {
      if (isToolUIPart(p) && p.state === 'input-streaming') {
        while (parts.length && parts[parts.length - 1].type === 'reasoning') parts.pop();
        continue;
      }
      parts.push(incomplete(p)
        ? { ...p, state: 'output-error', errorText: 'Stopped by user' }
        : p);
    }
    return { ...m, parts };
  })
  .filter((m) => m.role !== 'assistant' || (m.parts?.length ?? 0) > 0);

/**
 * Decide what actually goes over the wire for a send: which slice of history
 * (from the newest replayable compaction forward), whether to attach an OpenAI
 * compaction block to the body, and which older tool inputs/outputs to strip.
 *
 * Compactions are model-scoped — the encrypted block 400s against any model
 * other than the one that produced it (legacy alias migration on load, or a
 * mid-conversation model switch). OpenAI blocks record their model on the data
 * part; Anthropic blocks can't carry a field, so the producing model is read
 * from the turn's usage stats. A mismatch (or an untagged block from before we
 * recorded models) falls back to the full uncompacted history with the stale
 * artifacts stripped.
 */
/**
 * The messages that survive leaving PII Mode: everything up to the boundary recorded when
 * the mode was switched on, and nothing after it.
 *
 * PII Mode's whole promise is that the applicant turns never reach disk, and localStorage is
 * written from an effect watching the message array. So the turns have to be dropped from
 * state in the same commit that clears the mode -- if the mode reads as off while the array
 * still holds them, that effect persists applicant data. Extracted here so the rule is
 * stated and tested in one place rather than living only in a click handler.
 *
 * Fails closed when the boundary is past the end of the array. That means the prefix it
 * indexed has shrunk -- the chat was cleared, or a stopped turn's message was pruned -- so
 * the index no longer identifies anything and the messages sitting below it may be applicant
 * turns. Dropping everything loses a little history; trusting the index leaks.
 */
export const messagesAfterLeavingPiiMode = <M extends TranscriptMessage>(
  msgs: M[],
  boundary: number,
): M[] => (boundary > msgs.length ? [] : msgs.slice(0, Math.max(0, boundary)));

export function prepareOutgoingMessages<M extends TranscriptMessage>(
  allMessages: M[],
  model: string,
  usageByMessageId: Record<string, { model?: string }>,
): { messages: M[]; openaiCompaction?: { id: string; encrypted_content: string } } {
  let messagesToSend = allMessages;
  let openaiCompaction: { id: string; encrypted_content: string } | undefined;
  const isAnthropic = model.startsWith('claude-');

  // Find the last compaction block for the current provider and send from there forward.
  for (let i = allMessages.length - 1; i >= 0; i--) {
    const msg = allMessages[i];
    if (msg.role !== 'assistant') continue;

    if (isAnthropic) {
      // Anthropic: compaction stored as text part with providerMetadata.
      if (msg.parts?.some((p: any) => p.providerMetadata?.anthropic?.type === 'compaction')) {
        if (usageByMessageId[msg.id]?.model === model) {
          messagesToSend = allMessages.slice(i);
        } else {
          messagesToSend = stripCompactionArtifacts(allMessages);
        }
        break;
      }
    } else {
      // OpenAI: compaction stored as tagged data part. The carrier message is
      // skipped — the encrypted_content is sent via body and injected by the
      // backend fetch interceptor; sending starts from the message after it.
      const compPart = msg.parts?.find((p: any) => p.type === 'data-openai-compaction');
      if (compPart) {
        const data = (compPart as any).data as OpenAICompactionData | undefined;
        if (data?.model === model) {
          openaiCompaction = { id: data.id, encrypted_content: data.encrypted_content };
          messagesToSend = allMessages.slice(i + 1);
        } else {
          messagesToSend = stripCompactionArtifacts(allMessages);
        }
        break;
      }
    }
  }

  // Strip completed tool inputs/outputs from older messages before the
  // outgoing API request. Saves I/O bandwidth in addition to input tokens —
  // provider-side strategies (Anthropic's `clear_tool_uses_20250919`) only act
  // after they receive the request, so we'd still pay to send the bytes. Local
  // UI keeps everything intact; only outgoing API messages are trimmed.
  //
  // For Anthropic, the backend ALSO configures `clear_tool_uses_20250919` as a
  // within-turn safety net for the latest tool-call cycle preserved here (e.g.
  // agent fans out many tool calls in one turn).
  const lastMsg = messagesToSend[messagesToSend.length - 1];
  const inToolContinuation = lastMsg?.role === 'assistant';
  const preserveFromIdx = inToolContinuation
    ? messagesToSend.reduce((last, msg, i) => msg.role === 'assistant' ? i : last, -1)
    : messagesToSend.length;

  messagesToSend = messagesToSend.map((msg, msgIdx) => {
    if (msg.role !== 'assistant' || msgIdx >= preserveFromIdx || !msg.parts) return msg;
    const hasToolPart = msg.parts.some((p: any) => isToolUIPart(p));
    if (!hasToolPart) return msg;
    return {
      ...msg,
      parts: msg.parts.map((p: any) => {
        if (!isToolUIPart(p)) return p;
        const stripped: typeof p = { ...p, input: { stripped: true } };
        if (p.state === 'output-available') {
          stripped.output = { stripped: true };
        }
        return stripped;
      }),
    };
  });

  return { messages: messagesToSend, openaiCompaction };
}
